import {ValueResolver} from '@/engine/types';
import {recursiveReplaceIndex} from '@/engine/utils/object';
import {ALL_J_ARRAYS, J_ARRAYS} from '@/model/src/sections/section-j-plus/constants';
import {StateReference} from '@/model/types';

const J_ARRAYS_WITH_MERGED_INDEXES = ALL_J_ARRAYS.filter(array => array !== J_ARRAYS.J_guaranteedInterestAccounts);

type JMergedAccountArray = (typeof J_ARRAYS_WITH_MERGED_INDEXES)[number];

const J_ARRAY_MERGE_KEY: Partial<Record<JMergedAccountArray, string | null>> = {
  [J_ARRAYS.J_dailyInterestAccounts]: null,
  [J_ARRAYS.J_highYieldAccounts]: null,
  [J_ARRAYS.J_benevaInvestmentAccounts]: 'J32#{$index}',
  [J_ARRAYS.J_benevaGuaranteedInvestmentFunds]: 'J39#{$index}',
};

type AccountIndexMap = {
  pdfIndex: number;
  accountCode: string;
  engineIndexes: number[];
};

/*
 * Map accounts to pdf indexes by merging accounts that share the same fund code.
 */
const getAccountIndexes = (valueResolver: ValueResolver, arrayName: JMergedAccountArray): AccountIndexMap[] => {
  const accountsCount = Number(valueResolver({$ref: `_${arrayName}[count]`}));
  const accounts: AccountIndexMap[] = [];
  const mergeKey = J_ARRAY_MERGE_KEY[arrayName] ?? null;

  for (let index = 0; index < accountsCount; index++) {
    const accountCode = mergeKey === null ? '' : valueResolver({$ref: recursiveReplaceIndex(mergeKey, index)});

    const existingAccount = accounts.find(account => account.accountCode === accountCode);

    if (existingAccount) {
      existingAccount.engineIndexes.push(index);
    } else {
      accounts.push({
        pdfIndex: accounts.length,
        accountCode,
        engineIndexes: [index],
      });
    }
  }
  return accounts;
};

/*
 * Determines whether a reference relates to a J-section accounts array.
 * If it does, the PDF index is treated as a logical index rather than the index used by the engine.
 * This merges accounts with identical codes (or a single account for arrays without a code) that appear across different deposit types.
 * Specifically, resolving a PDF index from 0 to 9 will yield the merged accounts, which may be distributed across up to 40 different entries in the engine.
 */
export const transformReferenceForJArrays = (
  reference: StateReference,
  valueResolver: ValueResolver,
): StateReference | undefined => {
  if (!reference.$ref.includes('#')) return reference;

  const [, fieldSuffix] = reference.$ref.split('#');
  const fieldIndex = Number(fieldSuffix.includes('[') ? fieldSuffix.split('[')[0] : fieldSuffix);

  for (const arrayName of J_ARRAYS_WITH_MERGED_INDEXES) {
    // Try to retrieve an array row using the PDF index
    const fieldsEntry = valueResolver({$ref: `_${arrayName}#${fieldIndex}[fields]`});

    // If no row exists for this index in this array
    if (!fieldsEntry) continue;

    // Check if the field is part of this array row
    const fields = fieldsEntry.split(',');
    if (!fields.includes(reference.$ref)) continue;

    // Confirmed: field belongs to this J array — resolve via PDF index
    const accountIndexes = getAccountIndexes(valueResolver, arrayName);

    const accountIndexMap = accountIndexes.find(account => account.pdfIndex === fieldIndex);
    if (!accountIndexMap) return undefined;

    for (const engineIndex of accountIndexMap.engineIndexes) {
      const engineReference = {...reference, $ref: reference.$ref.replace(`#${fieldIndex}`, `#${engineIndex}`)};
      const value = valueResolver(engineReference); // Not using the visibilitySet is intentional
      if (value != '') {
        return engineReference;
      }
    }

    return undefined;
  }

  return reference;
};

/*
 * Transforms the reference to resolve according to specific rules:
 * 1. If the reference belongs to a J account array, merge duplicate accounts before resolving (except for CIG).
 * ...
 */
export const transformReference = (reference: StateReference, valueResolver: ValueResolver) => {
  if (reference.$ref.match(/^([A-Z]+\d+)#\d+$/)) {
    return transformReferenceForJArrays(reference, valueResolver);
  }

  return reference;
};
